import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Package, CalendarPlus, Sparkles, Loader2 } from 'lucide-react';
import { queryKeys } from '@/lib/api/queryKeys';
import { ensureGameLink, type GameLink } from '@/features/events/defaultJourney';
import { useQuickStartState } from '@/features/brand/useQuickStartState';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import { SampleGameDialog } from '@/features/brand/SampleGameDialog';
import { GameLinkPanel } from '@/features/brand/GameLinkPanel';

/**
 * The three-step happy path (task 8.2): add a xotski, create an event, get the
 * game link.
 *
 * Everything between step two and a live game — a station, a journey, a game,
 * the brand's colours — is a default the manager would have picked anyway, so
 * step three picks them (see `ensureGameLink`). They can change any of it
 * afterwards; what they cannot do is guess that those steps existed.
 *
 * A finished account does not need a checklist of things it already has, so the
 * home swaps this for a summary — but only on a LATER visit. Within the visit
 * where the path is walked, the card stays and hands over the link.
 */
export function QuickStart({
  brandId,
  enabled,
  onAddXotski,
  onAddEvent,
}: {
  brandId: number;
  enabled: boolean;
  onAddXotski: () => void;
  onAddEvent: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [link, setLink] = useState<GameLink | null>(null);
  const [sampleOpen, setSampleOpen] = useState(false);

  const { hasXotski, hasEvent, targetEvent, link: wiredLink, loading } =
    useQuickStartState(brandId, enabled);

  // `link` is what step three just produced; `wiredLink` is what the station
  // already had. The local one wins so the URL appears without a refetch.
  const liveLink = link ?? wiredLink;
  const hasLink = !!liveLink;

  const goLive = useMutation({
    mutationFn: async () => {
      if (!targetEvent) throw new Error(t('quickStart.needEvent'));
      const result = await ensureGameLink(targetEvent, brandId);
      if (!result) throw new Error(t('quickStart.noTemplate'));
      return result;
    },
    onSuccess: (result) => {
      setLink(result);
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byBrand(brandId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(targetEvent!.id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.stockAllocations.byEvent(targetEvent!.id),
      });
    },
    onError: (e) =>
      toast({ tone: 'error', title: t('quickStart.failed'), description: (e as Error).message }),
  });


  // Deliberately does NOT hide itself when the three steps complete. It used
  // to, and the effect was absurd: pressing "get my link" revealed the URL and
  // unmounted the card that was showing it, in the same frame. Whether a
  // returning visitor sees this card at all is the home's decision, made once
  // on load; this component's job is to stay put and finish the sentence.
  if (!enabled || loading) return null;

  return (
    <section className="mt-6 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-bg-surface)] p-5">
      <header className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[var(--color-accent-ink)]" />
        <h2
          className="text-sm font-extrabold uppercase tracking-widest text-[var(--color-xk-text)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('quickStart.title')}
        </h2>
      </header>

      <ol className="space-y-2">
        <Step
          index={1}
          done={hasXotski}
          icon={<Package className="h-4 w-4" />}
          title={t('quickStart.step1')}
          hint={t('quickStart.step1Hint')}
          actionLabel={t('quickStart.step1Cta')}
          onAction={onAddXotski}
          active={!hasXotski}
        />
        <Step
          index={2}
          done={hasEvent}
          icon={<CalendarPlus className="h-4 w-4" />}
          title={t('quickStart.step2')}
          hint={t('quickStart.step2Hint')}
          actionLabel={t('quickStart.step2Cta')}
          onAction={onAddEvent}
          active={hasXotski && !hasEvent}
        />
        <Step
          index={3}
          done={hasLink}
          icon={<ExternalLink className="h-4 w-4" />}
          title={t('quickStart.step3')}
          hint={t('quickStart.step3Hint')}
          actionLabel={t('quickStart.step3Cta')}
          onAction={() => goLive.mutate()}
          active={hasEvent && !hasLink}
          pending={goLive.isPending}
        />
      </ol>

      {/* Task 8.4 — opt-in only. A demo nobody asked for is a salesman; one
          behind a button they pressed answers "what does this look like?". */}
      <div className="mt-3 text-center">
        <button
          type="button"
          onClick={() => setSampleOpen(true)}
          className="text-xs font-semibold text-[var(--color-accent-ink)] underline-offset-2 hover:underline"
        >
          {t('sampleGame.cta')}
        </button>
      </div>
      <SampleGameDialog open={sampleOpen} onOpenChange={setSampleOpen} brandId={brandId} />

      {liveLink && <div className="mt-4"><GameLinkPanel link={liveLink} /></div>}

    </section>
  );
}

function Step({
  index,
  done,
  active,
  pending,
  icon,
  title,
  hint,
  actionLabel,
  onAction,
}: {
  index: number;
  done: boolean;
  active: boolean;
  pending?: boolean;
  icon: React.ReactNode;
  title: string;
  hint: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors',
        done
          ? 'border-transparent bg-[var(--color-xk-bg-deep)]'
          : active
            ? 'border-[var(--color-accent-ink)] bg-[var(--color-bg-input)]'
            : 'border-[var(--color-xk-border)] bg-transparent',
      )}
    >
      <span
        className={cn(
          'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold',
          done
            ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
            : 'border border-[var(--color-xk-border)] text-[var(--color-xk-text-subtle)]',
        )}
      >
        {done ? <Check className="h-3.5 w-3.5" /> : index}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'flex items-center gap-1.5 text-sm font-semibold',
            done
              ? 'text-[var(--color-xk-text-subtle)] line-through'
              : 'text-[var(--color-xk-text)]',
          )}
        >
          {icon}
          {title}
        </span>
        {!done && (
          <span className="block text-xs text-[var(--color-xk-text-subtle)]">{hint}</span>
        )}
      </span>

      {!done && (
        <button
          type="button"
          onClick={onAction}
          disabled={!active || pending}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity',
            active
              ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)] hover:opacity-90'
              : 'cursor-not-allowed border border-[var(--color-xk-border)] text-[var(--color-xk-text-subtle)] opacity-60',
          )}
        >
          {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {actionLabel}
        </button>
      )}
    </li>
  );
}
